import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import './Category.scss';
import agent from '../../../agent';
import { useLocation, useNavigate } from 'react-router-dom';
import SideModal from '../../Layouts/SideModal';
import CreateCategory from './CreateCategory';

function CategoryList({ currentUser }) {
  const location = useLocation();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [successMsg, setSuccessMsg] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Format date as Today, Yesterday, or MM/DD/YYYY
  const formatDate = (dateString) => {
    if (!dateString) return '--';
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  };

  const getCategoriesData = async (currentPageData = 1) => {
    setSuccessMsg(null);
    setErrorMsg(null);
    setLoading(true);
    try {
      const response = await agent.categories.getCategoriesData(
        search,
        currentPageData,
        pageSize,
        sortField,
        sortOrder
      );

      if (response && response.isSuccess && response.data && response.data.categories) {
        const mapped = response.data.categories.data.map(d => ({
          id: d.id,
          name: d.name || '--',
          description: d.description || '--',
          itemCount: d.item_count ?? '--',
          status: d.status || 'Active',
          createdAt: formatDate(d.created_at),
        }));

        setTableData(mapped);

        const pagination = response.data.categories.pagination || {};
        setTotalCount(pagination.totalCount || mapped.length);
        setTotalPages(pagination.totalPages || 1);
        setCurrentPage(parseInt(pagination.currentPage) || 1);
        setPageSize(pagination.pageSize || 30);
      } else {
        setTableData([]);
        setErrorMsg(response?.message || 'No data found');
        setTotalCount(0);
        setTotalPages(1);
        setCurrentPage(1);
      }
    } catch (error) {
      setTableData([]);
      setTotalCount(0);
      setTotalPages(1);
      setCurrentPage(1);
      setErrorMsg(
        error?.response?.body?.message || error?.message || 'Something went wrong'
      );
    } finally {
      setLoading(false);
    }
  };

  // Fetch on pagination / sort changes
  useEffect(() => {
    getCategoriesData(currentPage);
    // eslint-disable-next-line
  }, [currentPage, pageSize, sortField, sortOrder]);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
    getCategoriesData(1);
    // eslint-disable-next-line
  }, [search]);

  // Handle URL query parameters for success / error messages
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const successParam = urlParams.get('success');
    const errorParam = urlParams.get('error');

    if (successParam) {
      setSuccessMsg(decodeURIComponent(successParam));
      const params = new URLSearchParams(location.search);
      params.delete('success');
      navigate(`${location.pathname}${params.toString() ? '?' + params.toString() : ''}`, { replace: true });
    }

    if (errorParam) {
      setErrorMsg(decodeURIComponent(errorParam));
      const params = new URLSearchParams(location.search);
      params.delete('error');
      navigate(`${location.pathname}${params.toString() ? '?' + params.toString() : ''}`, { replace: true });
    }
  }, [location.search, navigate, location.pathname]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  const sortIcon = (field) => {
    if (sortField !== field) return null;
    return sortOrder === 'asc' ? ' ↑' : ' ↓';
  };

  const handleEditCategory = (category) => {
    setSelectedCategory(category);
    setIsCreateModalOpen(true);
  };

  const handleDeleteCategory = async (category) => {
    if (!window.confirm(`Are you sure you want to delete "${category.name}"?`)) return;

    try {
      setLoading(true);
      await agent.categories.deleteCategory(category.id);
      setSuccessMsg(`"${category.name}" deleted successfully`);
      await getCategoriesData(currentPage);
    } catch (error) {
      setErrorMsg('Failed to delete category');
      console.error('Delete error:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderPageNumbers = () => {
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    const pages = [];

    if (startPage > 1) {
      pages.push(
        <button key={1} onClick={() => setCurrentPage(1)} className={currentPage === 1 ? 'active' : ''}>1</button>
      );
      pages.push(<span key="start-ellipsis" className="pagination-ellipsis">...</span>);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button key={i} onClick={() => setCurrentPage(i)} className={currentPage === i ? 'active' : ''}>{i}</button>
      );
    }

    if (endPage < totalPages) {
      pages.push(<span key="end-ellipsis" className="pagination-ellipsis">...</span>);
      pages.push(
        <button key={totalPages} onClick={() => setCurrentPage(totalPages)} className={currentPage === totalPages ? 'active' : ''}>{totalPages}</button>
      );
    }

    return pages;
  };

  const startIdx = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endIdx = Math.min(currentPage * pageSize, totalCount);

  return (
    <div className="category-list-container p-0">
      {successMsg && <div className="success-message">{successMsg}</div>}
      {errorMsg && <div className="error-message">{errorMsg}</div>}

      <div className="category-list-header d-flex align-items-center gap-3 p-4 flex-nowrap">
        <input
          className="form-control max-w-full category-list-search"
          type="text"
          placeholder="Search categories..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <button
          className="category-list-create-btn btn btn-primary text-sm px-2 btn-custom px-3 py-2 m-0"
          onClick={() => {
            setSelectedCategory(null);
            setIsCreateModalOpen(true);
          }}
        >
          + Create Category
        </button>
      </div>

      <div className="category-list-table-wrapper">
        {loading && (
          <div className="category-list-loading">
            <div className="spinner"></div>
            <span>Loading...</span>
          </div>
        )}

        <table className="category-list-table table">
          <thead className="table-light">
            <tr>
              <th className="p-3 text-start fw-bold" scope="col">#</th>
              <th className="p-3 text-start fw-bold" scope="col" onClick={() => handleSort('name')}>Name&nbsp;{sortIcon('name')}</th>
              <th className="p-3 text-start fw-bold" scope="col" onClick={() => handleSort('status')}>Status&nbsp;{sortIcon('status')}</th>
              <th className="p-3 text-start fw-bold" scope="col" onClick={() => handleSort('createdAt')}>Created&nbsp;{sortIcon('createdAt')}</th>
              <th className="p-3 text-start fw-bold" scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {tableData.map((row, index) => (
              <tr key={row.id}>
                <td data-label="#">
                  {(currentPage - 1) * pageSize + index + 1}
                </td>
                <td data-label="Name">{row.name}</td>
                <td data-label="Status">
                  <span className={`status-badge status-${row.status.toLowerCase()}`}>
                    {row.status}
                  </span>
                </td>
                <td data-label="Created">{row.createdAt}</td>
                <td data-label="Actions">
                  <div className="action-buttons">
                    <button
                      className="btn-edit"
                      onClick={() => handleEditCategory(row)}
                      title="Edit Category"
                    >
                      ✏️
                    </button>
                    <button
                      className="btn-delete"
                      onClick={() => handleDeleteCategory(row)}
                      title="Delete Category"
                    >
                      🗑️
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {tableData.length === 0 && !loading && (
              <tr>
                <td colSpan="7" className="no-data-row">No categories found</td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="pagination-info px-4 py-3 text-sm text-black-50 text-end border-top m-0">
          {totalCount > 0 ? `Showing ${startIdx}–${endIdx} of ${totalCount}` : ''}
        </div>

        {totalPages > 1 && (
          <div className="pagination-controls">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              Prev
            </button>
            <div className="pagination-pages">{renderPageNumbers()}</div>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Create / Edit Category Modal */}
      <SideModal
        isOpen={isCreateModalOpen}
        setIsOpen={setIsCreateModalOpen}
        showButton={false}
        classDef="create-category-modal"
      >
         <CreateCategory
          onClose={() => {
            setIsCreateModalOpen(false);
            setSelectedCategory(null);
          }}
          currentUser={currentUser}
          onSuccess={() => getCategoriesData(currentPage)}
          editCategory={selectedCategory}
        />
      </SideModal>
    </div>
  );
}

const mapStateToProps = (state) => ({
  currentUser: state.auth.currentUser,
});

export default connect(mapStateToProps)(CategoryList);